import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';

const VacuumPrimeCalculator = () => {
    // Input states
    const [elevation, setElevation] = useState('500');
    const [totalLift, setTotalLift] = useState('8');
    const [pipeLength, setPipeLength] = useState('60');
    const [pipeInternalDiameter, setPipeInternalDiameter] = useState('12');
    const [vacPumpCFM, setVacPumpCFM] = useState('1.25');
    const [vacPumpMaxVac, setVacPumpMaxVac] = useState('25');
    const [manifoldInternalDiameter, setManifoldInternalDiameter] = useState('12');
    const [manifoldLength, setManifoldLength] = useState('8');
    const [numberOfPumps, setNumberOfPumps] = useState('2');
    
    // Additional state for pipe type selection
    const [pipeType, setPipeType] = useState('steel');
    const [pipeSize, setPipeSize] = useState('4"');
    const [hdpeSdr, setHdpeSdr] = useState('SDR 11');
    
    // State for pump selection
    const [selectedPumpIndex, setSelectedPumpIndex] = useState('-1');

    // Calculation states
    const [totalTime, setTotalTime] = useState('');
    const [errorEstimate, setErrorEstimate] = useState('');
    const [patmAbs, setPatmAbs] = useState('29.4');
    const [patmGauge, setPatmGauge] = useState('0.5');
    const [pReqAbs, setPReqAbs] = useState('23.0');
    const [pReqGauge, setPReqGauge] = useState('7');
    const [pipeDiam, setPipeDiam] = useState('12');
    const [volume, setVolume] = useState('');
    const [weightedCFM, setWeightedCFM] = useState('');
    const [volumeManifold, setVolumeManifold] = useState('');
    const [cfmMultiplier, setCfmMultiplier] = useState('');
    
    // Pipe type and size data
    const pipeData = {
        hdpe: [
            { size: '4" SDR 11', id: 3.63 },
            { size: '4" SDR 17', id: 3.94 },
            { size: '5" SDR 11', id: 4.49 },
            { size: '5" SDR 17', id: 4.87 },
            { size: '6" SDR 11', id: 5.35 },
            { size: '6" SDR 17', id: 5.80 },
            { size: '8" SDR 11', id: 6.96 },
            { size: '8" SDR 17', id: 7.55 },
            { size: '10" SDR 11', id: 8.68 },
            { size: '10" SDR 17', id: 9.41 },
            { size: '12" SDR 11', id: 10.29 },
            { size: '12" SDR 17', id: 11.16 },
            { size: '14" SDR 11', id: 11.30 },
            { size: '14" SDR 17', id: 12.25 },
            { size: '16" SDR 11', id: 12.92 },
            { size: '16" SDR 17', id: 14.01 }
        ],
        steel: [
            { size: '4"', id: 4.03 },
            { size: '5"', id: 5.05 },
            { size: '6"', id: 6.07 },
            { size: '8"', id: 7.98 },
            { size: '10"', id: 10.02 },
            { size: '12"', id: 11.94 },
            { size: '14"', id: 13.13 },
            { size: '16"', id: 15.00 }
        ]
    };
    
    // Vacuum pump data
    const pumpData = [
        { partNumber: '46-10-0100', gastPn: '1HAB-10-M100X', flow: 1.3, maxVac: 25, type: 'PISTON' },
        { partNumber: '46-04-0741', gastPn: '1023-251Q-G279', flow: 10, maxVac: 28, type: 'ROTARY VANE' },
        { partNumber: '46-04-0740', gastPn: '1423-103Q-G625', flow: 13, maxVac: 26, type: 'ROTARY VANE' },
        { partNumber: '46-04-0744', gastPn: '5LCA-251-M550NGX', flow: 4.2, maxVac: 25, type: 'PISTON' }
    ];
    
    // Get unique HDPE sizes
    const uniqueHdpeSizes = [...new Set(pipeData.hdpe.map(pipe => pipe.size.split(' ')[0]))];
    
    // Handler for pipe type change
    const handlePipeTypeChange = (e) => {
        const newPipeType = e.target.value;
        setPipeType(newPipeType);
        
        // Reset size selections based on pipe type
        if (newPipeType === 'hdpe') {
            setPipeSize('4"');
            setHdpeSdr('SDR 11');
            // Set internal diameter for HDPE 4" SDR 11
            setPipeInternalDiameter('3.63');
        } else {
            setPipeSize('4"');
            // Set internal diameter for Steel 4"
            setPipeInternalDiameter('4.03');
        }
    };
    
    // Handler for pipe size change
    const handlePipeSizeChange = (e) => {
        const newSize = e.target.value;
        setPipeSize(newSize);
        
        // Update internal diameter based on pipe type and size
        if (pipeType === 'hdpe') {
            const hdpePipe = pipeData.hdpe.find(p => p.size === `${newSize} ${hdpeSdr}`);
            if (hdpePipe) {
                setPipeInternalDiameter(hdpePipe.id.toString());
            }
        } else {
            const steelPipe = pipeData.steel.find(p => p.size === newSize);
            if (steelPipe) {
                setPipeInternalDiameter(steelPipe.id.toString());
            }
        }
    };
    
    // Handler for HDPE SDR change
    const handleHdpeSdrChange = (e) => {
        const newSdr = e.target.value;
        setHdpeSdr(newSdr);
        
        // Update internal diameter based on pipe size and new SDR
        if (pipeType === 'hdpe') {
            const hdpePipe = pipeData.hdpe.find(p => p.size === `${pipeSize} ${newSdr}`);
            if (hdpePipe) {
                setPipeInternalDiameter(hdpePipe.id.toString());
            }
        }
    };
    
    // Handler for pump selection
    const handlePumpSelection = (e) => {
        const index = e.target.value;
        setSelectedPumpIndex(index);
        
        if (index !== '-1') {
            const selectedPump = pumpData[parseInt(index)];
            setVacPumpCFM(selectedPump.flow.toString());
            setVacPumpMaxVac(selectedPump.maxVac.toString());
        }
    };

    // Elevation to atmospheric pressure mapping table
    const elevationToPressureMap = [
        { elevation: -2000, pressure: 32.15 },
        { elevation: -1000, pressure: 31.02 },
        { elevation: 0, pressure: 29.92 },
        { elevation: 500, pressure: 29.4 },
        { elevation: 1000, pressure: 28.86 },
        { elevation: 1500, pressure: 28.3 },
        { elevation: 2000, pressure: 27.8 },
        { elevation: 2500, pressure: 27.3 },
        { elevation: 3000, pressure: 26.8 },
        { elevation: 3500, pressure: 26.3 },
        { elevation: 4000, pressure: 25.8 },
        { elevation: 4500, pressure: 25.4 },
        { elevation: 5000, pressure: 24.9 },
        { elevation: 6000, pressure: 24.0 },
        { elevation: 7000, pressure: 23.1 },
    ];
    
    // P-ATM abs to gauge pressure mapping
    const pAtmGaugeMap = [
        { abs: 32.15, gauge: 0 },
        { abs: 31.02, gauge: 0 },
        { abs: 29.92, gauge: 0 },
        { abs: 29.4, gauge: 0.5 },
        { abs: 28.86, gauge: 1 },
        { abs: 28.3, gauge: 1.5 },
        { abs: 27.8, gauge: 2 },
        { abs: 27.3, gauge: 2.5 },
        { abs: 26.8, gauge: 3 },
        { abs: 26.3, gauge: 3.5 },
        { abs: 25.8, gauge: 4 },
        { abs: 25.4, gauge: 4.5 },
        { abs: 24.9, gauge: 5 },
        { abs: 24.0, gauge: 6 },
        { abs: 23.1, gauge: 7 }
    ];
    
    // P-REQ abs to gauge pressure mapping
    const pReqGaugeMap = [
        { abs: 30.00, gauge: 0.00 },
        { abs: 29.00, gauge: 1.00 },
        { abs: 28.00, gauge: 2.00 },
        { abs: 27.00, gauge: 3.00 },
        { abs: 26.00, gauge: 4.00 },
        { abs: 25.00, gauge: 5.00 },
        { abs: 24.00, gauge: 6.00 },
        { abs: 23.00, gauge: 7.00 },
        { abs: 22.00, gauge: 8.00 },
        { abs: 21.00, gauge: 9.00 },
        { abs: 20.00, gauge: 10.00 },
        { abs: 19.00, gauge: 11.00 },
        { abs: 18.00, gauge: 12.00 },
        { abs: 17.00, gauge: 13.00 },
        { abs: 16.00, gauge: 14.00 },
        { abs: 15.00, gauge: 15.00 },
        { abs: 14.00, gauge: 16.00 },
        { abs: 13.00, gauge: 17.00 },
        { abs: 12.00, gauge: 18.00 },
        { abs: 11.00, gauge: 19.00 },
        { abs: 10.00, gauge: 20.00 },
        { abs: 9.00, gauge: 21.00 },
        { abs: 8.00, gauge: 22.00 },
        { abs: 7.00, gauge: 23.00 },
        { abs: 6.00, gauge: 24.00 },
        { abs: 5.00, gauge: 25.00 },
        { abs: 4.00, gauge: 26.00 },
        { abs: 3.00, gauge: 27.00 },
        { abs: 2.00, gauge: 28.00 },
        { abs: 1.00, gauge: 29.00 },
        { abs: 1.00, gauge: 29.10 },
        { abs: 1.00, gauge: 29.20 },
        { abs: 1.00, gauge: 29.30 },
        { abs: 1.00, gauge: 29.40 },
        { abs: 1.00, gauge: 29.50 },
        { abs: 1.00, gauge: 29.60 },
        { abs: 1.00, gauge: 29.70 },
        { abs: 1.00, gauge: 29.80 }
    ];

    // Function to get atmospheric pressure based on elevation
    const getAtmosphericPressure = useCallback((elevationValue) => {
        elevationValue = Number(elevationValue);
        // Find the closest elevation points
        let lowerBound = elevationToPressureMap[0];
        let upperBound = elevationToPressureMap[elevationToPressureMap.length - 1];
        
        for (let i = 0; i < elevationToPressureMap.length; i++) {
            const entry = elevationToPressureMap[i];
            if (entry.elevation <= elevationValue) {
                lowerBound = entry;
            }
            if (entry.elevation >= elevationValue && entry.elevation < upperBound.elevation) {
                upperBound = entry;
            }
        }
        
        // If exact match found
        if (lowerBound.elevation === elevationValue) {
            return lowerBound.pressure;
        }
        
        // Linear interpolation between closest points
        if (upperBound.elevation === lowerBound.elevation) {
            return lowerBound.pressure;
        }
        
        const ratio = (elevationValue - lowerBound.elevation) / (upperBound.elevation - lowerBound.elevation);
        const pressure = lowerBound.pressure - (ratio * (lowerBound.pressure - upperBound.pressure));
        
        return pressure;
    }, [elevationToPressureMap]);
    
    // Function to get gauge pressure from abs pressure using the lookups
    const getGaugePressure = useCallback((absValue, isAtm = true) => {
        absValue = Number(absValue);
        const lookupTable = isAtm ? pAtmGaugeMap : pReqGaugeMap;
        
        // Find exact match
        const exactMatch = lookupTable.find(entry => Math.abs(entry.abs - absValue) < 0.01);
        if (exactMatch) {
            return exactMatch.gauge;
        }
        
        // Find closest values for interpolation
        let lowerEntry = lookupTable[0];
        let upperEntry = lookupTable[lookupTable.length - 1];
        
        for (let i = 0; i < lookupTable.length; i++) {
            if (lookupTable[i].abs <= absValue) {
                lowerEntry = lookupTable[i];
            }
            if (lookupTable[i].abs >= absValue && lookupTable[i].abs < upperEntry.abs) {
                upperEntry = lookupTable[i];
            }
        }
        
        // If absValue is outside the range, use the closest entry
        if (absValue <= lowerEntry.abs) return lowerEntry.gauge;
        if (absValue >= upperEntry.abs) return upperEntry.gauge;
        
        // Linear interpolation
        const ratio = (absValue - lowerEntry.abs) / (upperEntry.abs - lowerEntry.abs);
        const gauge = lowerEntry.gauge + ratio * (upperEntry.gauge - lowerEntry.gauge);
        
        return gauge;
    }, [pAtmGaugeMap, pReqGaugeMap]);

    const calculateTime = useCallback(() => {
        try {
            // Validate inputs
            if (!pipeInternalDiameter || !pipeLength || !vacPumpCFM || !vacPumpMaxVac || 
                !manifoldInternalDiameter || !manifoldLength || !numberOfPumps || !elevation || !totalLift) {
                setTotalTime('Incomplete Data');
                setErrorEstimate('');
                return;
            }
            
            // Convert all inputs to numbers for calculation
            const elevationNum = Number(elevation);
            const totalLiftNum = Number(totalLift);
            const pipeLengthNum = Number(pipeLength);
            const pipeInternalDiameterNum = Number(pipeInternalDiameter);
            const vacPumpCFMNum = Number(vacPumpCFM);
            const vacPumpMaxVacNum = Number(vacPumpMaxVac);
            const manifoldInternalDiameterNum = Number(manifoldInternalDiameter);
            const manifoldLengthNum = Number(manifoldLength);
            const numberOfPumpsNum = Number(numberOfPumps);
            
            // Step 1: Get P-ATM from elevation lookup
            const pAtmValue = getAtmosphericPressure(elevationNum);
            setPatmAbs(pAtmValue.toFixed(2));
            
            // Get P-ATM gauge from lookup
            const pAtmGaugeValue = getGaugePressure(pAtmValue, true);
            setPatmGauge(pAtmGaugeValue.toFixed(2));
            
            // Step 2: Calculate P-REQ = Roundup(P-ATM-(.88*Total Lift),0)
            const pReqAbsValue = Math.ceil(pAtmValue - (0.88 * totalLiftNum));
            setPReqAbs(pReqAbsValue.toFixed(2));
            
            // Get P-REQ gauge from lookup
            const pReqGaugeValue = getGaugePressure(pReqAbsValue, false);
            setPReqGauge(pReqGaugeValue.toFixed(2));
            
            // Step 3: Calculate Volume Manifold = PI*(Manifold Internal Diameter/24)^2*Manifold Length
            const manifoldVol = Math.PI * Math.pow(manifoldInternalDiameterNum / 24, 2) * manifoldLengthNum;
            setVolumeManifold(manifoldVol.toFixed(5));
            
            // Step 4: Calculate Volume = (PI*(Pipe Internal Diameter/24)^2*Pipe Length) + Volume Manifold + (Number of Pumps*1.5)
            const pipeVol = Math.PI * Math.pow(pipeInternalDiameterNum / 24, 2) * pipeLengthNum;
            const totalVolume = pipeVol + manifoldVol + (numberOfPumpsNum * 1.5);
            setVolume(totalVolume.toFixed(5));
            setPipeDiam(pipeInternalDiameterNum.toString());
            
            // Step 5: Calculate cfm multiplier = ((Vac Pump CFM*(Vac Pump Max/P-ATM))*.9)
            const cfmMult = ((vacPumpCFMNum * (vacPumpMaxVacNum / pAtmValue)) * 0.9);
            setCfmMultiplier(cfmMult.toFixed(5));
            
            // Calculate weighted CFM
            const wCFM = vacPumpCFMNum; // Using nominal CFM for now
            setWeightedCFM(wCFM.toFixed(5));
            
            // Step 6: Calculate Total Time: ((Volume*P-ATM*LN(P-ATM/P-REQ))/(5*Vac Pump CFM))/1440
            // Use natural logarithm (ln)
            const timeInDays = ((totalVolume * pAtmValue * Math.log(pAtmValue / pReqAbsValue)) / 
                               (5 * vacPumpCFMNum)) / 1440;
                               
            // Convert to hours and minutes
            const totalMinutes = timeInDays * 24 * 60; // Convert from days to minutes
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.floor(totalMinutes % 60);
            
            // Format as HH:MM
            setTotalTime(`${hours}:${minutes.toString().padStart(2, '0')}`);
            
            // Calculate 10% error estimate (in seconds)
            const errorSeconds = Math.round(totalMinutes * 60 * 0.1); // 10% of total time in seconds
            const errorHours = Math.floor(errorSeconds / 3600);
            const errorMinutes = Math.floor((errorSeconds % 3600) / 60);
            const errorSecs = errorSeconds % 60;
            
            // Format as HH:MM:SS
            setErrorEstimate(`${errorHours}:${errorMinutes.toString().padStart(2, '0')}:${errorSecs.toString().padStart(2, '0')}`);
            
        } catch (error) {
            console.error('Vacuum prime calculation error:', error);
            setTotalTime('Error');
            setErrorEstimate('Error');
        }
    }, [
        elevation,
        totalLift,
        pipeInternalDiameter, 
        pipeLength, 
        vacPumpCFM,
        vacPumpMaxVac,
        manifoldInternalDiameter, 
        manifoldLength, 
        numberOfPumps,
        getAtmosphericPressure,
        getGaugePressure
    ]);

    // Update elevation and recalculate when inputs change
    useEffect(() => {
        calculateTime();
    }, [calculateTime]);

    return (
        <div className="container-fluid py-3">
            <Row>
                <Col lg={{span: 10, offset: 1}} md={12}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5" className="bg-primary text-white text-center">
                            TIME ESTIMATE TO PRIME PUMP STATION USING VACUUM PRIME
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    {/* Pipe Type Selection */}
                                    <Card className="mb-3">
                                        <Card.Header className="bg-light">Pipe Size Selection</Card.Header>
                                        <Card.Body>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label><strong>Pipe Type:</strong></Form.Label>
                                                        <Form.Select 
                                                            value={pipeType}
                                                            onChange={handlePipeTypeChange}
                                                            size="sm"
                                                        >
                                                            <option value="steel">Schedule 40 Steel</option>
                                                            <option value="hdpe">HDPE</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label><strong>Size:</strong></Form.Label>
                                                        <Form.Select 
                                                            value={pipeSize}
                                                            onChange={handlePipeSizeChange}
                                                            size="sm"
                                                        >
                                                            {pipeType === 'steel' 
                                                                ? pipeData.steel.map((pipe, index) => (
                                                                    <option key={index} value={pipe.size}>{pipe.size}</option>
                                                                ))
                                                                : uniqueHdpeSizes.map((size, index) => (
                                                                    <option key={index} value={size}>{size}</option>
                                                                ))
                                                            }
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                
                                                {pipeType === 'hdpe' && (
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label><strong>SDR:</strong></Form.Label>
                                                            <Form.Select 
                                                                value={hdpeSdr}
                                                                onChange={handleHdpeSdrChange}
                                                                size="sm"
                                                            >
                                                                <option value="SDR 11">SDR 11</option>
                                                                <option value="SDR 17">SDR 17</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                    
                                    {/* Vacuum Pump Selection */}
                                    <Card className="mb-3">
                                        <Card.Header className="bg-light">Vacuum Pump Selection</Card.Header>
                                        <Card.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Label><strong>Select Vacuum Pump:</strong></Form.Label>
                                                <Form.Select 
                                                    value={selectedPumpIndex}
                                                    onChange={handlePumpSelection}
                                                    size="sm"
                                                >
                                                    <option value="-1">Custom Values</option>
                                                    {pumpData.map((pump, index) => (
                                                        <option key={index} value={index}>
                                                            {pump.partNumber} - {pump.gastPn} ({pump.type}, {pump.flow} CFM, {pump.maxVac} in Hg)
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                
                                    <Table bordered responsive>
                                        <tbody>
                                            <tr>
                                                <td className="fw-bold">ELEVATION (FT)</td>
                                                <td className="bg-light" width="120px">
                                                    <Form.Control
                                                        type="number"
                                                        value={elevation}
                                                        onChange={(e) => setElevation(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">TOTAL LIFT (FT) *</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={totalLift}
                                                        onChange={(e) => setTotalLift(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">PIPE LENGTH (FT) **</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={pipeLength}
                                                        onChange={(e) => setPipeLength(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">PIPE INTERNAL DIAMETER (IN) ***</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={pipeInternalDiameter}
                                                        onChange={(e) => setPipeInternalDiameter(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">VAC PUMP CFM ****</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={vacPumpCFM}
                                                        onChange={(e) => setVacPumpCFM(e.target.value)}
                                                        step="0.01"
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">VAC PUMP MAX VAC (in Hg)****</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={vacPumpMaxVac}
                                                        onChange={(e) => setVacPumpMaxVac(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">MANIFOLD INTERNAL DIAMETER (IN) ******</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={manifoldInternalDiameter}
                                                        onChange={(e) => setManifoldInternalDiameter(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">MANIFOLD LENGTH (FT)</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={manifoldLength}
                                                        onChange={(e) => setManifoldLength(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">NUMBER OF PUMPS</td>
                                                <td className="bg-light">
                                                    <Form.Control
                                                        type="number"
                                                        value={numberOfPumps}
                                                        onChange={(e) => setNumberOfPumps(e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={4}>
                                    <Card className="mb-3 bg-light">
                                        <Card.Body className="text-center">
                                            <Card.Title>TOTAL TIME (HRS:MIN)</Card.Title>
                                            <h2 className="my-3">{totalTime || "—"}</h2>
                                        </Card.Body>
                                    </Card>
                                    <Card className="bg-light">
                                        <Card.Body className="text-center">
                                            <Card.Title>10% ERROR ESTIMATE (HR:MIN:S)</Card.Title>
                                            <h4 className="my-3">{errorEstimate || "—"}</h4>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <hr className="my-4" />
                            
                            <h5 className="mt-4 mb-3">Calculation Details</h5>
                            <Table bordered responsive size="sm">
                                <thead className="bg-light">
                                    <tr>
                                        <th></th>
                                        <th>abs</th>
                                        <th>gauge</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="fw-bold">P-ATM</td>
                                        <td>{patmAbs}</td>
                                        <td>{patmGauge}</td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">P-REQ</td>
                                        <td>{pReqAbs}</td>
                                        <td>{pReqGauge}</td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">PIPE DIAM.</td>
                                        <td>{pipeDiam}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">VOLUME</td>
                                        <td>{volume}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">WEIGHTED CFM</td>
                                        <td>{weightedCFM}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">VOLUME MANIFOLD</td>
                                        <td>{volumeManifold}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold">cfm multiplier</td>
                                        <td>{cfmMultiplier}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </Table>
                            
                            <Alert variant="info" className="mt-4">
                                <small>
                                    * Total lift is the vertical distance from water level to centerline of pump<br/>
                                    ** Pipe length is the total horizontal distance from the water source to pump<br/>
                                    *** Use actual internal diameter of pipe<br/>
                                    **** VAC PUMP CFM is the vacuum pump flowrate in cubic feet per minute<br/>
                                    ***** VAC PUMP MAX VAC is the maximum vacuum the pump can pull in inches of mercury<br/>
                                    ****** MANIFOLD INTERNAL DIAMETER is the actual internal diameter of the pump manifold
                                </small>
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default VacuumPrimeCalculator;